import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { updateConfirmationNumber, updateCurrentStep } from '../../../state/actions/add-address.action';
import { select, Store } from '@ngrx/store';
import { StepIndicatorModel, AddressModel } from '@app/state/models/add-address.model';
import { firstValueFrom, Observable } from 'rxjs';
import {
  selectSendAccounts,
  selectStepIndicatorIndex,
  selectAddAddress,
  selectOriginalAddressSnapshot,
} from '../../../state/selectors/add-address.selector';
import { Account } from '@app/models/thirdparty-documents.model';
import { ThirdPartyDocumentsService } from '../../../services/thirdparty-documents/thirdparty-documents.service';
import { ModalDialogComponent } from '@vg-constellation/angular-19/modal-dialog';
import { AemUtil } from '@app/utils/aem.util';
import { ThirdpartyDocumentsAemItem } from '@app/models/aem.model';
import {
  ACCOUNTS_AVAILABLE_FOR_SELECTION_TABLE,
  ADD_ADDRESS_ERROR_MESSAGE,
  CANCEL_BUTTON,
  CONTINUE_BUTTON,
  EDIT_ADDRESS_ERROR_MESSAGE,
  NEW_ADDRESS_LABEL,
  NO_CHANGES_DETECTED_ERROR_MESSAGE,
  REVIEW_AND_SUBMIT_PAGE_CONTAINER,
  SELECT_ACCOUNT_TABLE_LABEL,
  SHOW_LESS_LABEL,
  SHOW_MORE_LABEL, SUBMIT_BUTTON,
} from '@app/constants/aem.constant';

@Component({
  selector: 'app-review-and-submit',
  templateUrl: './review-and-submit.component.html',
  styleUrls: ['./review-and-submit.component.scss'],
  standalone: false,
})
export class ReviewAndSubmitComponent implements OnInit {
  labels = {
    heading: '',
    reviewMessage: '',
    newAddressLabel: '',
    selectedAccountsLabel: '',
    symbol: '',
    name: '',
    fundAccount: '',
    errorMessage: '',
    showMore: '',
    showLess: '',
    selectAccountTableLabel: '',
    continueButton: '',
    cancelButton: '',
    submitButton: '',
  };
  @Input() thirdpartyDocumentsAemContent: ThirdpartyDocumentsAemItem;
  currentStepIndicatorIndex$: Observable<number>;
  private allAccounts$: Observable<Account[]>;
  address$: Observable<AddressModel>;
  isLoaderIndicate: boolean = false;
  accounts: any;
  address: any;
  associatedAccounts: any[] = [];
  allAccounts: any;
  errorMessage: string;
  showNoChangesError = false;
  noChangesErrorMessage = 'Please make at least one change to the address or account selection before submitting.';
  private originalAddressSnapshot: AddressModel | null = null;
  @ViewChild('errorAddModal') errorModal: ModalDialogComponent;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private _router: Router,
    private store: Store<StepIndicatorModel>,
    private readonly thirdPartyDocumentsService: ThirdPartyDocumentsService,
  ) {
  }

  ngOnInit(): void {
    this.currentStepIndicatorIndex$ = this.store.pipe(select(selectStepIndicatorIndex));
    this.address$ = this.store.pipe(select(selectAddAddress));
    this.address$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.address = data;
      this.associatedAccounts = data.associatedAccounts;
    });
    this.allAccounts$ = this.store.pipe(select(selectSendAccounts));
    this.allAccounts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.allAccounts = JSON.parse(JSON.stringify(data.accounts));
      this.accounts = JSON.parse(JSON.stringify(data.accounts));
      this.accounts = this.accounts.filter((account: any) => {
        if (account.productType.includes('BROKERAGE')) {
          return account.isChecked === true;
        }
        account.positions = (account.positions || []).filter((pos: any) => pos.isChecked === true);
        return account.positions.length > 0;
      });
    });
    this.store.pipe(select(selectOriginalAddressSnapshot), takeUntilDestroyed(this.destroyRef)).subscribe((snapshot) => {
      this.originalAddressSnapshot = snapshot;
    });
    this.getAemContent();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private getAemContent(): void {
    this.labels.heading = AemUtil.getPageContainerHeading(this.thirdpartyDocumentsAemContent, REVIEW_AND_SUBMIT_PAGE_CONTAINER);
    this.labels.reviewMessage = AemUtil.getInfoMessage(this.thirdpartyDocumentsAemContent, REVIEW_AND_SUBMIT_PAGE_CONTAINER);
    this.labels.newAddressLabel = AemUtil.getLabelTextFromLabels(this.thirdpartyDocumentsAemContent, NEW_ADDRESS_LABEL);
    this.labels.selectedAccountsLabel = AemUtil.getSelectedAccountLable(this.thirdpartyDocumentsAemContent, REVIEW_AND_SUBMIT_PAGE_CONTAINER);
    const selectionPageFields = AemUtil.getTableColumnLabels(this.thirdpartyDocumentsAemContent, ACCOUNTS_AVAILABLE_FOR_SELECTION_TABLE);
    this.labels.symbol = selectionPageFields.symbol;
    this.labels.name = selectionPageFields.name;
    this.labels.fundAccount = selectionPageFields.fundAccount;
    this.labels.showMore = AemUtil.getLabelTextFromLabels(this.thirdpartyDocumentsAemContent, SHOW_MORE_LABEL);
    this.labels.showLess = AemUtil.getLabelTextFromLabels(this.thirdpartyDocumentsAemContent, SHOW_LESS_LABEL);
    this.labels.selectAccountTableLabel = AemUtil.getLabelTextFromLabels(this.thirdpartyDocumentsAemContent, SELECT_ACCOUNT_TABLE_LABEL);
    this.labels.continueButton = AemUtil.getPrimaryCtaLabel(this.thirdpartyDocumentsAemContent, CONTINUE_BUTTON);
    this.labels.cancelButton = AemUtil.getPrimaryCtaLabel(this.thirdpartyDocumentsAemContent, CANCEL_BUTTON);
    this.labels.submitButton = AemUtil.getPrimaryCtaLabel(this.thirdpartyDocumentsAemContent, SUBMIT_BUTTON);
    const noChangesMsg = this.thirdpartyDocumentsAemContent?.messages?.find(m => m._path?.includes(NO_CHANGES_DETECTED_ERROR_MESSAGE));
    if (noChangesMsg?.body) this.noChangesErrorMessage = AemUtil.getHtmlTextFromBodyChild(noChangesMsg.body);
  }

  handleClick = (event: any) => {
    const expandedState = event.currentTarget.getAttribute('aria-expanded') === 'true';
    event.currentTarget.setAttribute('title', expandedState ? this.labels.showMore : this.labels.showLess);
    event.currentTarget.setAttribute('aria-expanded', String(!expandedState));
    if (!expandedState) {
      event.currentTarget.parentElement.parentElement.classList.add('c11n-accordion--expanded');
    } else {
      event.currentTarget.parentElement.parentElement.classList.remove('c11n-accordion--expanded');
    }
  };

  async goToPage(event: any, route?: string) {
    if (this.isLoaderIndicate) return;
    this.showNoChangesError = false;
    const isAdd = (this.associatedAccounts?.length ?? 0) === 0;

    const nameLines = [
      this.address.name,
      this.address.nameLine2 || '',
      this.address.nameLine3 || '',
      this.address.nameLine4 || '',
    ].filter(v => v && v.trim());

    const addrLines = [
      this.address.addressLine1 || '',
      this.address.addressLine2 || '',
    ].filter(v => v && v.trim());

    const allLines = [...nameLines, ...addrLines];

    const payload: any = {
      numberNameLines: nameLines.length,
      addressLine1: allLines[0] || '',
      addressLine2: allLines[1] || '',
      addressLine3: allLines[2] || '',
      addressLine4: allLines[3] || '',
      addressLine5: allLines[4] || '',
      foreignAddress: this.address.foreignAddress,
    };

    if (this.address.foreignAddress) {
      payload.foreignCountryCode = this.address.foreignCountryCode;
      payload.foreignCountryName = this.address.foreignCountryName;
      payload.city = null;
      payload.state = null;
      payload.zipCode = null;
      payload.zipCodePlusFour = null;
    } else {
      payload.city = this.address.city;
      payload.state = this.address.state;
      payload.zipCode = this.address.zipCode;
      payload.zipCodePlusFour = this.address.zipCodePlusFour;
      payload.foreignCountryCode = null;
      payload.foreignCountryName = null;
    }

    if (isAdd) {
      payload.interestedPartyAccountsToAdd = [];
      this.accounts.forEach((account: any) => {
        if (account.productType.includes('BROKERAGE')) {
          payload.interestedPartyAccountsToAdd.push({
            accountId: account.accountId,
            interestedPartyDocumentPreference: account.optedFor,
          });
        } else if (account.productType.includes('MUTUAL_FUND')) {
          account.positions.forEach((position: any) => {
            payload.interestedPartyAccountsToAdd.push({
              accountId: account.accountId,
              vastAccountNumber: position.vastAccountNumber,
              interestedPartyDocumentPreference: position.optedFor,
            });
          });
        }
      });
    } else {
      payload.interestedPartyAccountsToAdd = [];
      payload.interestedPartyAccountsToUpdate = [];
      payload.interestedPartyAccountsToDelete = [];

      this.associatedAccounts.forEach((assocAccount: any) => {
        this.allAccounts.forEach((account: any) => {
          if (account.productType.includes('BROKERAGE')) {
            if (account.accountId === assocAccount.accountId) {
              if (account.isChecked) {
                payload.interestedPartyAccountsToUpdate.push({
                  accountId: account.accountId,
                  interestedPartyId: assocAccount.interestedPartyId,
                  interestedSequenceNumber: assocAccount.interestedSequenceNumber,
                  interestedPartyDocumentPreference: account.optedFor,
                });
              } else {
                payload.interestedPartyAccountsToDelete.push({
                  accountId: account.accountId,
                  interestedPartyId: assocAccount.interestedPartyId,
                  interestedSequenceNumber: assocAccount.interestedSequenceNumber,
                  interestedPartyDocumentPreference: assocAccount.interestedPartyDocumentPreference,
                });
              }
              account.isLooped = true;
            }
          } else if (account.productType.includes('MUTUAL_FUND')) {
            account.positions.forEach((position: any) => {
              if (position.vastAccountNumber === assocAccount.accountId) {
                if (position.isChecked) {
                  payload.interestedPartyAccountsToUpdate.push({
                    accountId: account.accountId,
                    vastAccountNumber: position.vastAccountNumber,
                    interestedPartyId: assocAccount.interestedPartyId,
                    interestedSequenceNumber: assocAccount.interestedSequenceNumber,
                    interestedPartyDocumentPreference: position.optedFor,
                  });
                } else {
                  payload.interestedPartyAccountsToDelete.push({
                    accountId: account.accountId,
                    vastAccountNumber: position.vastAccountNumber,
                    interestedPartyId: assocAccount.interestedPartyId,
                    interestedSequenceNumber: assocAccount.interestedSequenceNumber,
                    interestedPartyDocumentPreference: assocAccount.interestedPartyDocumentPreference,
                  });
                }
                position.isLooped = true;
              }
            });
          }
        });
      });

      this.allAccounts.forEach((account: any) => {
        if (account.productType.includes('BROKERAGE')) {
          if (account.isChecked && !account.isLooped) {
            payload.interestedPartyAccountsToAdd.push({
              accountId: account.accountId,
              interestedPartyDocumentPreference: account.optedFor,
            });
          }
        } else if (account.productType.includes('MUTUAL_FUND')) {
          account.positions.forEach((position: any) => {
            if (position.isChecked && !position.isLooped) {
              payload.interestedPartyAccountsToAdd.push({
                accountId: account.accountId,
                vastAccountNumber: position.vastAccountNumber,
                interestedPartyDocumentPreference: position.optedFor,
              });
            }
          });
        }
      });
    }

    if (!isAdd && this.originalAddressSnapshot) {
      const addressChanged = this.hasAddressChanged(this.originalAddressSnapshot);
      const accountsAdded = payload.interestedPartyAccountsToAdd.length > 0;
      const accountsDeleted = payload.interestedPartyAccountsToDelete.length > 0;
      const preferencesChanged = payload.interestedPartyAccountsToUpdate.some((updated: any) => {
        const original = this.associatedAccounts.find((a: any) =>
          a.interestedPartyId === updated.interestedPartyId &&
          a.interestedSequenceNumber === updated.interestedSequenceNumber
        );
        return !original || original.interestedPartyDocumentPreference !== updated.interestedPartyDocumentPreference;
      });
      if (!addressChanged && !accountsAdded && !accountsDeleted && !preferencesChanged) {
        this.showNoChangesError = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    try {
      this.isLoaderIndicate = true;
      let res: any;
      if (isAdd) {
        this.errorMessage = AemUtil.getErrorMessagesFromMessages(this.thirdpartyDocumentsAemContent?.messages, ADD_ADDRESS_ERROR_MESSAGE);
        res = await firstValueFrom(this.thirdPartyDocumentsService.addAddress(payload));
      } else {
        this.errorMessage = AemUtil.getErrorMessagesFromMessages(this.thirdpartyDocumentsAemContent?.messages, EDIT_ADDRESS_ERROR_MESSAGE);
        res = await firstValueFrom(this.thirdPartyDocumentsService.updateAddress(payload));
      }
      this.isLoaderIndicate = false;
      const operationResult = isAdd
        ? (res?.data?.interestedPartyAddressCreate ?? res?.interestedPartyAddressCreate)
        : (res?.data?.interestedPartyAddressUpdate ?? res?.interestedPartyAddressUpdate);
      if (operationResult && !operationResult.errorCode) {
        this.store.dispatch(updateConfirmationNumber({ confirmationNumber: operationResult.confirmationNumber ?? '' }));
        this.store.dispatch(updateCurrentStep({ currentStepIndex: 5 }));
        if (route) this._router.navigate([route]);
      } else {
        this.errorMessage = operationResult?.errorMessage || res?.errors?.[0]?.message || res?.errorMessage || this.errorMessage;
        this.errorModal.openModalDialog(event);
      }
    } catch (error: any) {
      this.isLoaderIndicate = false;
      const errBody = error?.error;
      const operationResult = isAdd
        ? (errBody?.data?.interestedPartyAddressCreate ?? errBody?.interestedPartyAddressCreate)
        : (errBody?.data?.interestedPartyAddressUpdate ?? errBody?.interestedPartyAddressUpdate);
      if (operationResult && !operationResult.errorCode && operationResult.confirmationNumber) {
        this.store.dispatch(updateConfirmationNumber({ confirmationNumber: operationResult.confirmationNumber }));
        this.store.dispatch(updateCurrentStep({ currentStepIndex: 5 }));
        if (route) this._router.navigate([route]);
      } else {
        this.errorMessage = operationResult?.errorMessage || this.errorMessage;
        this.errorModal.openModalDialog(event);
      }
    }
  }

  private hasAddressChanged(original: AddressModel): boolean {
    const curr = this.address;
    return (
      curr.name !== original.name ||
      (curr.nameLine2 || '') !== (original.nameLine2 || '') ||
      (curr.nameLine3 || '') !== (original.nameLine3 || '') ||
      (curr.nameLine4 || '') !== (original.nameLine4 || '') ||
      curr.addressLine1 !== original.addressLine1 ||
      (curr.addressLine2 || '') !== (original.addressLine2 || '') ||
      (curr.city || '') !== (original.city || '') ||
      curr.state !== original.state ||
      (curr.zipCode || '') !== (original.zipCode || '') ||
      (curr.zipCodePlusFour || '') !== (original.zipCodePlusFour || '') ||
      curr.foreignAddress !== original.foreignAddress ||
      (curr.foreignCountryCode || '') !== (original.foreignCountryCode || '') ||
      (curr.foreignCountryName || '') !== (original.foreignCountryName || '')
    );
  }
}
